import { useEffect, useState } from "react";

import { Check, Copy, Download, Key, Link2, Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentUserData } from "@/features/auth/hooks/useCurrentUserData";
import { useUserPrefixDicom } from "@/features/imagings/hooks/useImaging";
import {
  useCreateInstallLink,
  useMarkInstallLinkUsed,
} from "@/features/installations/hooks/useInstallLinks";
import {
  MODULES,
  usePreconfiguredDownload,
  type ModuleId,
} from "@/features/installations/hooks/usePreconfiguredDownload";
import {
  endpointsForModules,
  STUDY_DOWNLOAD_BRIDGE_MODULE_ID,
} from "@/features/installations/constants/installationDefaults";
import { InstallationInstructions } from "./InstallationInstructions";
import { useApiKeysByUser, useCreateApiKey } from "@/features/users/hooks/useApiKeys";
import { EndpointCommandPicker } from "@/features/users/components/EndpointCommandPicker";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";
import { BREAKPOINTS, useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { cn } from "@/shared/lib/utils";

// ---------------------------------------------------------------------------
// Shared form logic (used by both Dialog and BottomSheet)
// ---------------------------------------------------------------------------

interface InstallFormProps {
  user: User;
  onClose: () => void;
  onDownloaded?: () => void;
}

function InstallForm({ user, onClose, onDownloaded }: InstallFormProps) {
  // El creador del enlace es la sesión de Firebase Auth. Las cuentas de equipo
  // viven en `staff`, no en `users`, así que `userData` suele ser null y no
  // puede usarse para habilitar el botón ni para `createdBy`.
  const { authUser } = useCurrentUserData();
  const displayName = getUserFullName(user);

  // Fetch prefix DICOM from Imaging API
  const { data: prefixDicom } = useUserPrefixDicom(user.uid);

  // Form state
  const [selectedModules, setSelectedModules] = useState<ModuleId[]>([
    "pacs",
    "router",
    "visor",
    STUDY_DOWNLOAD_BRIDGE_MODULE_ID,
    "his",
    "ris",
  ]);
  const [subdomain, setSubdomain] = useState("");
  const [useCustomDomain, setUseCustomDomain] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [createdApiKeyInfo, setCreatedApiKeyInfo] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [apiKeyMode, setApiKeyMode] = useState<"none" | "existing" | "new">("none");
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>("");
  const [selectedEndpoints, setSelectedEndpoints] = useState<string[]>(() =>
    endpointsForModules([
      "pacs",
      "router",
      "visor",
      STUDY_DOWNLOAD_BRIDGE_MODULE_ID,
      "his",
      "ris",
    ])
  );

  // Existing API keys for the destination institution
  const { data: apiKeys, isLoading: isLoadingApiKeys } = useApiKeysByUser(user.uid);

  // Computed final domain
  const finalDomain = useCustomDomain
    ? customDomain
    : subdomain
      ? `${subdomain}.viewmed-services.com`
      : "";

  // Mutations
  const downloadMutation = usePreconfiguredDownload();
  const createLinkMutation = useCreateInstallLink();
  const markUsedMutation = useMarkInstallLinkUsed();
  const createApiKeyMutation = useCreateApiKey();

  useEffect(() => {
    if (apiKeyMode === "new") {
      setSelectedEndpoints(endpointsForModules(selectedModules));
    }
  }, [apiKeyMode, selectedModules]);

  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const isTablet = useMediaQuery(BREAKPOINTS.tablet);
  const useBottomSheet = isMobile || isTablet;

  const toggleModule = (moduleId: ModuleId) => {
    setSelectedModules((prev) => {
      if (prev.includes(moduleId)) {
        const next = prev.filter((m) => m !== moduleId);
        if (moduleId === "pacs") {
          return next.filter((m) => m !== STUDY_DOWNLOAD_BRIDGE_MODULE_ID);
        }
        return next;
      }

      const next: ModuleId[] = [...prev, moduleId];
      if (
        moduleId === "pacs" &&
        !next.includes(STUDY_DOWNLOAD_BRIDGE_MODULE_ID)
      ) {
        next.push(STUDY_DOWNLOAD_BRIDGE_MODULE_ID);
      }
      return next;
    });
  };

  const handleDownload = async () => {
    if (selectedModules.length === 0) return;
    if (!finalDomain.trim()) return;

    const apiKeyPayload =
      apiKeyMode !== "none" && selectedApiKeyId
        ? { apiKeyId: selectedApiKeyId }
        : {};

    await downloadMutation.mutateAsync({
      user,
      storageOwnerUid: user.uid,
      modules: selectedModules,
      caddyDomain: finalDomain.trim(),
      prefixDicom: prefixDicom || "",
      ...apiKeyPayload,
    });
    onDownloaded?.();
    if (!useBottomSheet) onClose();
  };

  const handleGenerateLink = async () => {
    if (selectedModules.length === 0) return;
    if (!finalDomain.trim()) return;
    if (!authUser?.uid) return;

    const resolvedApiKeyName =
      apiKeyMode === "existing"
        ? apiKeys?.find((k) => k.id === selectedApiKeyId)?.name
        : apiKeyMode === "new" && createdApiKeyInfo?.id === selectedApiKeyId
          ? createdApiKeyInfo.name
          : undefined;

    const apiKeyPayload =
      apiKeyMode !== "none" && selectedApiKeyId
        ? {
            apiKeyId: selectedApiKeyId,
            ...(resolvedApiKeyName ? { apiKeyName: resolvedApiKeyName } : {}),
          }
        : {};

    try {
      const linkId = await createLinkMutation.mutateAsync({
        institutionUid: user.uid,
        institutionName: displayName,
        modules: selectedModules,
        caddyDomain: finalDomain.trim(),
        createdBy: authUser.uid,
        prefixDicom: prefixDicom || undefined,
        ...apiKeyPayload,
      });

      setGeneratedLink(`${window.location.origin}/install-link/${linkId}`);
    } catch {
      // El toast de error lo emite useCreateInstallLink.
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateApiKey = async () => {
    if (selectedEndpoints.length === 0) return;

    try {
      const result = await createApiKeyMutation.mutateAsync({
        name: `VIEWMED_API_KEY - ${displayName || user.uid}`,
        permissions: selectedEndpoints,
        user: user.uid,
      });
      setSelectedApiKeyId(result.id);
      setCreatedApiKeyInfo({ id: result.id, name: result.name });
    } catch (error) {
      console.error("Error generating API key:", error);
    }
  };

  const isFormReady = selectedModules.length > 0 && finalDomain.trim().length > 0;
  const canGenerateLink = isFormReady && Boolean(authUser?.uid);
  const isLoading =
    downloadMutation.isPending ||
    createLinkMutation.isPending ||
    markUsedMutation.isPending;

  const CloseButton = ({
    onClick,
    className,
  }: {
    onClick: () => void;
    className?: string;
  }) => (
    <button
      onClick={onClick}
      className={cn(
        "p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors",
        className
      )}
    >
      <X className="h-4 w-4" />
    </button>
  );

  const formatApiKeyDate = (date: string) =>
    new Date(date).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const AiConfigSection = ({ className }: { className?: string }) => (
    <div className={cn("space-y-3", className)}>
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          API Key para servicios{" "}
          <span className="font-normal text-muted-foreground">(opcional)</span>
        </p>
        <p className="text-xs text-muted-foreground">
          Puedes omitirla y no bloqueará la creación de los archivos de instalación. Si seleccionas o creas una clave, se vinculará por ID para configurar los servicios correspondientes.
        </p>
      </div>

      <RadioGroup
        value={apiKeyMode}
        onValueChange={(value) => {
          const mode = value as "none" | "existing" | "new";
          setApiKeyMode(mode);
          if (mode === "none") {
            setSelectedApiKeyId("");
            setCreatedApiKeyInfo(null);
            setSelectedEndpoints([]);
          } else if (mode === "existing") {
            setCreatedApiKeyInfo(null);
            setSelectedApiKeyId("");
          } else {
            setSelectedApiKeyId(createdApiKeyInfo?.id || "");
            setSelectedEndpoints(endpointsForModules(selectedModules));
          }
        }}
        className="space-y-3"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="none" id="ai-key-none" />
          <Label
            htmlFor="ai-key-none"
            className="text-xs font-medium text-gray-600 dark:text-gray-400"
          >
            Sin API Key
          </Label>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="existing" id="ai-key-existing" />
            <Label
              htmlFor="ai-key-existing"
              className="text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Usar API Key existente
            </Label>
          </div>
          {apiKeyMode === "existing" && (
            <Select
              value={selectedApiKeyId}
              onValueChange={setSelectedApiKeyId}
              disabled={isLoadingApiKeys || !apiKeys?.length}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    isLoadingApiKeys
                      ? "Cargando API Keys..."
                      : apiKeys?.length
                        ? "Selecciona una API Key"
                        : "No hay API Keys disponibles"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {apiKeys?.map((apiKey) => (
                  <SelectItem key={apiKey.id} value={apiKey.id}>
                    {apiKey.name} ({formatApiKeyDate(apiKey.createdAt)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="new" id="ai-key-new" />
            <Label
              htmlFor="ai-key-new"
              className="text-xs font-medium text-gray-600 dark:text-gray-400"
            >
              Crear nueva API Key
            </Label>
          </div>
          {apiKeyMode === "new" && (
            <>
              {createdApiKeyInfo && selectedApiKeyId === createdApiKeyInfo.id ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-green-800 dark:text-green-300 font-medium">
                    <Check className="h-4 w-4 text-green-600 shrink-0" />
                    <span>API Key creada y vinculada correctamente:</span>
                  </div>
                  <div className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
                    {createdApiKeyInfo.name} ({createdApiKeyInfo.id})
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setCreatedApiKeyInfo(null);
                      setSelectedApiKeyId("");
                    }}
                    className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground"
                  >
                    Crear otra clave diferente
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Endpoints autorizados
                    </Label>
                    <EndpointCommandPicker
                      value={selectedEndpoints}
                      onChange={setSelectedEndpoints}
                    />
                    <p className="text-xs text-muted-foreground">
                      {selectedEndpoints.length} endpoint(s) seleccionado(s)
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGenerateApiKey}
                    disabled={createApiKeyMutation.isPending || selectedEndpoints.length === 0}
                    className="w-full"
                  >
                    {createApiKeyMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="h-4 w-4 mr-2" />
                    )}
                    Generar API Key
                  </Button>
                </div>
              )}
              {createApiKeyMutation.isError && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  No se pudo generar la API Key. Intenta de nuevo.
                </p>
              )}
            </>
          )}
        </div>
      </RadioGroup>
    </div>
  );

  // -----------------------------------------------------------------------
  // BottomSheet layout
  // -----------------------------------------------------------------------
  if (useBottomSheet) {
    return (
      <>
        <div
          className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[1px]"
          onClick={onClose}
        />
        <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up safe-bottom flex justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-strong border-t border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto custom-scrollbar">
            {/* Handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-gray-700/50">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                  Descargar Instalaciones
                </h3>
                <p className="text-[11px] text-muted-foreground truncate">{displayName}</p>
              </div>
              <CloseButton onClick={onClose} />
            </div>

            {/* Institution Summary */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700/50">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">UID:</span>
                  <p className="font-mono text-gray-900 dark:text-gray-100 truncate">
                    {user.uid}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Prefix DICOM:</span>
                  <p className="font-mono text-gray-900 dark:text-gray-100 truncate">
                    {prefixDicom || "—"}
                  </p>
                </div>
              </div>
            </div>

            {/* Modules */}
            <div className="px-5 py-3">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Módulos a incluir
              </p>
              <div className="space-y-2">
                {MODULES.map((mod) => (
                  <label
                    key={mod.id}
                    className="flex items-center gap-3 cursor-pointer py-1"
                  >
                    <Checkbox
                      checked={selectedModules.includes(mod.id)}
                      onCheckedChange={() => toggleModule(mod.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {mod.name}
                      </span>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {mod.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* CADDY_DOMAIN */}
            <div>
              <Label
                htmlFor="caddy-domain"
                className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block"
              >
                Dominio Caddy (HTTPS) *
              </Label>

              <div className="flex items-center gap-2 mb-2">
                <Checkbox
                  id="custom-domain"
                  checked={useCustomDomain}
                  onCheckedChange={(checked) => setUseCustomDomain(checked as boolean)}
                />
                <label
                  htmlFor="custom-domain"
                  className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer"
                >
                  Usar dominio personalizado
                </label>
              </div>

              {useCustomDomain ? (
                <Input
                  id="caddy-domain"
                  type="text"
                  placeholder="pacs.hospital.com"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                />
              ) : (
                <div className="flex items-center gap-1">
                  <Input
                    id="subdomain"
                    type="text"
                    placeholder="prueba"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    .viewmed-services.com
                  </span>
                </div>
              )}

              {finalDomain && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-mono">
                  {finalDomain}
                </p>
              )}
            </div>

            {/* AI Configuration */}
            <AiConfigSection className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50" />

            {/* Generated Link */}
            {generatedLink && (
              <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2">
                  Link temporal generado (un solo uso)
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-gray-100 dark:bg-gray-700 rounded px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 truncate select-all">
                    {generatedLink}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyLink}
                    className="shrink-0 h-8"
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-green-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/30 space-y-2">
              <Button
                onClick={handleDownload}
                disabled={!isFormReady || isLoading}
                className="w-full h-10 text-sm font-bold rounded-xl"
              >
                {downloadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Descargando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Descargar Instalaciones
                  </>
                )}
              </Button>
              <Button
                onClick={handleGenerateLink}
                disabled={!canGenerateLink || isLoading}
                variant="outline"
                className="w-full h-10 text-sm font-bold rounded-xl"
              >
                {createLinkMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Generar Link Temporal
                  </>
                )}
              </Button>
            </div>

            {/* Installation Instructions */}
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700/50">
              <InstallationInstructions />
            </div>
          </div>
        </div>
      </>
    );
  }

  // -----------------------------------------------------------------------
  // Desktop Dialog layout
  // -----------------------------------------------------------------------
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Descargar Instalaciones
          </DialogTitle>
          <DialogDescription>
            Configuración para: <strong>{displayName}</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Institution Summary */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400 w-24 shrink-0">UID:</span>
            <code className="font-mono text-gray-900 dark:text-gray-100">{user.uid}</code>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 dark:text-gray-400 w-24 shrink-0">
              Prefix DICOM:
            </span>
            <code className="font-mono text-gray-900 dark:text-gray-100">
              {prefixDicom || "—"}
            </code>
          </div>
        </div>

        {/* Modules */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Módulos a incluir
          </p>
          <div className="space-y-2">
            {MODULES.map((mod) => (
              <label key={mod.id} className="flex items-center gap-3 cursor-pointer py-1">
                <Checkbox
                  checked={selectedModules.includes(mod.id)}
                  onCheckedChange={() => toggleModule(mod.id)}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {mod.name}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {mod.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* CADDY_DOMAIN */}
        <div>
          <Label
            htmlFor="caddy-domain"
            className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block"
          >
            Dominio Caddy (HTTPS) *
          </Label>

          <div className="flex items-center gap-2 mb-2">
            <Checkbox
              id="custom-domain"
              checked={useCustomDomain}
              onCheckedChange={(checked) => setUseCustomDomain(checked as boolean)}
            />
            <label
              htmlFor="custom-domain"
              className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer"
            >
              Usar dominio personalizado
            </label>
          </div>

          {useCustomDomain ? (
            <Input
              id="caddy-domain"
              type="text"
              placeholder="pacs.hospital.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
            />
          ) : (
            <div className="flex items-center gap-1">
              <Input
                id="subdomain"
                type="text"
                placeholder="prueba"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                .viewmed-services.com
              </span>
            </div>
          )}

          {finalDomain && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-mono">
              {finalDomain}
            </p>
          )}
        </div>

        {/* AI Configuration */}
        <AiConfigSection />

        {/* Generated Link */}
        {generatedLink && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2">
              Link temporal generado (un solo uso)
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 truncate select-all">
                {generatedLink}
              </code>
              <Button size="sm" variant="outline" onClick={handleCopyLink}>
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cerrar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!isFormReady || isLoading}
            className="min-w-[140px]"
          >
            {downloadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </>
            )}
          </Button>
          <Button
            onClick={handleGenerateLink}
            disabled={!canGenerateLink || isLoading}
            variant="secondary"
            className="min-w-[160px]"
          >
            {createLinkMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Generar Link
              </>
            )}
          </Button>
        </DialogFooter>

        {/* Installation Instructions */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <InstallationInstructions />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main export: DownloadInstallationsDialog
// ---------------------------------------------------------------------------

interface DownloadInstallationsDialogProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
}

export function DownloadInstallationsDialog({
  user,
  open,
  onClose,
}: DownloadInstallationsDialogProps) {
  if (!open || !user) return null;

  return <InstallForm user={user} onClose={onClose} onDownloaded={onClose} />;
}
